import { computed, ref, watch, type MaybeRefOrGetter, toValue } from 'vue';

/**
 * Keeps a dialog or a dropdown menu in the page only while it is open, and
 * while it fades out after closing.
 *
 * A closed reka-ui Dialog or DropdownMenuContent still renders an empty
 * <Teleport> to <body>. The section sidebar used to hold about eleven of them,
 * four for every saved-view row, and it removed a handful each time the icon
 * strip changed area. Once in about a thousand clicks Vue failed to remove one,
 * threw "Cannot read properties of null (reading 'nextSibling')", and left the
 * sidebar showing the old area. A dialog wrapped in this has no Teleport while
 * it is closed, so changing area has none to remove.
 *
 * Use it like this:
 *
 *     const { mounted, onCloseFinished } = useMountedWhileOpen(open);
 *     <Dialog v-if="mounted" v-model:open="open">
 *       <DialogContent @close-auto-focus="onCloseFinished">
 *
 * Reka fires close-auto-focus once the closing animation has finished and the
 * content has left the page. Waiting for it keeps the fade-out the user sees
 * today, and focus still goes back where it was.
 */
export function useMountedWhileOpen(open: MaybeRefOrGetter<boolean>) {
    const isClosing = ref(false);

    watch(
        () => toValue(open),
        (isOpen, wasOpen) => {
            isClosing.value = !isOpen && wasOpen;
        },
    );

    const mounted = computed(() => toValue(open) || isClosing.value);

    function onCloseFinished() {
        isClosing.value = false;
    }

    return { mounted, onCloseFinished };
}
